import React, { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Download } from "lucide-react";

// Lightbox modal for photo evidence.
// Props:
//   images: [{ url, filename }]
//   index: current image index (controlled by parent)
//   onClose: () => void
//   onIndexChange: (nextIndex) => void
// Keyboard: ESC closes, ← → navigate.
export default function ImageLightbox({ images, index, onClose, onIndexChange }) {
  const total = images?.length || 0;
  const current = images?.[index];

  const goPrev = useCallback(() => {
    if (total <= 1) return;
    onIndexChange((index - 1 + total) % total);
  }, [index, total, onIndexChange]);

  const goNext = useCallback(() => {
    if (total <= 1) return;
    onIndexChange((index + 1) % total);
  }, [index, total, onIndexChange]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handler);
    // Lock body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = prev;
    };
  }, [onClose, goPrev, goNext]);

  if (!current) return null;

  return (
    <div
      data-testid="image-lightbox"
      onClick={onClose}
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center"
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 h-14 flex items-center justify-between px-4 text-white pointer-events-none">
        <div className="flex items-center gap-3 pointer-events-auto">
          <span className="font-mono text-xs tracking-wider opacity-75">
            {index + 1} / {total}
          </span>
          <span className="text-sm font-medium truncate max-w-md" data-testid="lightbox-filename">
            {current.filename}
          </span>
        </div>
        <div className="flex items-center gap-2 pointer-events-auto">
          <a
            href={current.url}
            download
            onClick={(e) => e.stopPropagation()}
            className="h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Download"
            data-testid="lightbox-download-btn"
          >
            <Download size={16} />
          </a>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Close"
            data-testid="lightbox-close-btn"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Prev / Next arrows */}
      {total > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Previous"
            data-testid="lightbox-prev-btn"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Next"
            data-testid="lightbox-next-btn"
          >
            <ChevronRight size={22} />
          </button>
        </>
      )}

      {/* Image */}
      <img
        src={current.url}
        alt={current.filename}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[95vw] max-h-[85vh] object-contain rounded shadow-2xl select-none"
        data-testid="lightbox-image"
      />
    </div>
  );
}
